import { useMutation, useQuery } from '@apollo/client';
import ErrorPage from '@website/components/utilities/ErrorPage';
import LoadingPage from '@website/components/utilities/LoadingPage';
import {
  RemoveUserHostRoleMutationDocument,
  UserHostRolesQuery,
  UserHostRolesDocument,
} from '@website/types/graphqlOperations';
import React, { useState } from 'react';
import { Row, Col, Button, Alert, Container } from 'react-bootstrap';
import AddUserHostRole from './AddUserHostRole';

export type UsersHostRoles = UserHostRolesQuery['user']['hostRoles']['items'];

interface UserHostRoleItemProps {
  userID: string;
  userRole: UsersHostRoles[0];
  onError: (msg: string) => void;

  onRemove: () => void;
}

export function UserHostRoleItem({
  userID,
  userRole,
  onError,
  onRemove,
}: UserHostRoleItemProps) {
  const [removeUserHostRole, { loading }] = useMutation(
    RemoveUserHostRoleMutationDocument,
  );

  const removeUserRole = async (hostID: string) => {
    const { errors } = await removeUserHostRole({
      variables: {
        input: {
          hostID,
          userID,
        },
      },
    });

    if (errors) {
      onError('Something went wrong removing host roles');
      return;
    }
    onRemove();
  };

  return (
    <Row>
      <Col>{userRole.host.name}</Col>
      <Col>{userRole.role}</Col>
      <Col xs="12" md="auto">
        <Button
          onClick={() => removeUserRole(userRole.host.id)}
          variant="danger"
          disabled={loading}
        >
          <i className="fas fa-trash me-2" />
          {!loading ? 'Remove' : 'Removing'}
        </Button>
      </Col>
    </Row>
  );
}

interface UserHostRoleItemsProps {
  userID: string;
  userRoles: UsersHostRoles;
  onError: (msg: string) => void;
  onRemove: () => void;
}

export function UserHostRoleItems({
  userID,
  userRoles,
  onError,
  onRemove,
}: UserHostRoleItemsProps) {
  return (
    <>
      {userRoles.map((role) => (
        <Col key={role.host.id} className="p-1">
          <UserHostRoleItem
            userID={userID}
            userRole={role}
            onError={onError}
            onRemove={onRemove}
          />
        </Col>
      ))}
    </>
  );
}

interface ManageUserHostRolesProps {
  userID: string;
}

export default function ManageUserHostRoles({
  userID,
}: ManageUserHostRolesProps) {
  const [errorMessage, setErrorMessage] = useState('');

  const { data, error, loading, refetch } = useQuery(UserHostRolesDocument, {
    variables: {
      id: userID,
    },
    fetchPolicy: 'cache-and-network',
  });

  // incase of an error updating something
  const showErrorMessage = (errorMsg: string) => setErrorMessage(errorMsg);

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return <ErrorPage />;
  }

  const areUserHostRolesEmpty = data?.user?.hostRoles?.items?.length === 0;

  return (
    <Container>
      <Row>
        <Col className="pt-3">
          <h3>User Host Roles</h3>
          {errorMessage && (
            <Row>
              <Col>
                <Alert variant="danger">{errorMessage}</Alert>
              </Col>
            </Row>
          )}
          {data && data?.user?.hostRoles?.items && (
            <UserHostRoleItems
              userID={userID}
              userRoles={data.user.hostRoles.items}
              onError={showErrorMessage}
              onRemove={() => refetch()}
            />
          )}
          {areUserHostRolesEmpty && (
            <Row>
              <Col>
                <Alert variant="warning">
                  This user does not have permission to manage any hosts
                </Alert>
              </Col>
            </Row>
          )}
          <AddUserHostRole userID={userID} onRoleAdded={() => refetch()} />
        </Col>
      </Row>
    </Container>
  );
}
